import { ccfRoutes } from '@app/ccf/ccf-routes';
import { useCCF } from '@app/ccf/company-report/ccf-context';
import { useGetSupplyChainActivityList } from '@shared/api/hooks/ccf/activities/supply-chain-logistics';
import {
  CompanyReportsActivitiesFoodListFlowTypeItem,
  CompanyReportsActivitiesSupplyChainLogisticsListSortItem,
  SupplyChainLogisticsActivitiesList,
} from '@shared/api/types';
import { DataTable } from '@shared/components/data-table/data-table';
import { DataTableToolbar } from '@shared/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@shared/components/data-table/hooks/use-server-data-table';
import { baseTableSchema } from '@shared/components/data-table/schema';
import {
  DataTableFilterField,
  DataTableProps,
} from '@shared/components/data-table/types';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { supplyChainLogisticsTableColumns } from './supply-chain-logistics-table-columns';
import SupplyChainLogisticsTableToolbar from './supply-chain-logistics-table-toolbar';

interface FoodTableProps extends Partial<DataTableProps> {}

export const schema = baseTableSchema.extend({
  logisticsFlow: z.string().optional(),
});

const flowTypeOptions = [
  {
    label: 'Supply',
    value: CompanyReportsActivitiesFoodListFlowTypeItem.supply,
  },
  {
    label: 'Distribution',
    value: CompanyReportsActivitiesFoodListFlowTypeItem.distribution,
  },
];

export default function SupplyChainLogisticsTable({
  enableRowClick = true,
}: FoodTableProps) {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const search = schema.parse(Object.fromEntries(searchParams));
  const { uuid: reportUuid } = useCCF();

  const flowTypes = searchParams.get('flowType')?.split('.') as
    | CompanyReportsActivitiesFoodListFlowTypeItem[]
    | undefined;

  const { data, isPreviousData, isFetchedAfterMount, isLoading, isError } =
    useGetSupplyChainActivityList(reportUuid!, {
      page: search.page,
      page_size: search.page_size,
      search: search.logisticsFlow || '',
      flow_type: flowTypes,
      sort: [
        search.sort,
      ] as CompanyReportsActivitiesSupplyChainLogisticsListSortItem[],
    });

  if (isError) throw new Error();

  const { results: activities, count } = data || {};
  const pageCount = count ? Math.ceil(count / (search.page_size || 10)) : 0;

  const filterFields: DataTableFilterField<SupplyChainLogisticsActivitiesList>[] =
    [
      {
        label: 'Name',
        value: 'logisticsFlow',
        placeholder: 'Search item...',
      },
      {
        label: 'Type',
        value: 'flowType',
        options: flowTypeOptions,
      },
    ];

  const { table, context } = useServerDataTable({
    data: activities || [],
    columns: supplyChainLogisticsTableColumns,
    pageCount,
    filterFields,
    defaultPerPage: 10,
    rowId: 'uuid',
    columnVisibilityState: {
      uuid: false,
      period: false,
      site: false,
      weightOption: false,
    },
  });

  return (
    <>
      <DataTable
        table={table}
        context={context}
        enablePagination
        className="bg-gray-00"
        rowClassName={() =>
          enableRowClick ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'
        }
        onRowClick={(row) => {
          if (!enableRowClick) return;
          const type = row?.original.flowType;
          navigate(
            generatePath(
              ccfRoutes[
                `${type === 'distribution' ? `DISTRIBUTION` : `SUPPLY`}_FLOW_EDIT`
              ],
              {
                report_uuid: reportUuid,
                flow_uuid: row?.original.logisticsFlow.uuid || '',
              }
            )
          );
        }}
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
      >
        <DataTableToolbar table={table} filterFields={filterFields}>
          <SupplyChainLogisticsTableToolbar />
        </DataTableToolbar>
      </DataTable>
    </>
  );
}
